import {
    generateDynamicCoreQuestion,
    generateForkedQuestion,
    generateLinearQuestion,
    generatePollResult,
    generateThankYouCard,
    generateTitleCard,
} from '@dmp/qqms/survey-generators';
import { insert } from '@dmp/shared/helpers';
import { isQuestionItem } from '../../state-type-guards';
import type {
    AnswerState,
    CardState,
    DynamicCoreQuestionState,
    ForkedQuestionState,
    LinearQuestionState,
    PollResultCardState,
    StoreState,
    ThankYouCardState,
    TitleCardState,
} from '../../state-types';
import { generateAnswerShape } from '../utils/generate-answer-shape';
import { makeSurveyDirty } from '../utils/make-survey-dirty';

type AddCardResult = {
    nextState: StoreState;
    newCardId: string;
};

/**
 * Add a new card into they survey.
 * - if `insertAfterCardId` is given, the new card will be inserted after that card
 */
export const addCard =
    (
        cardType: CardState['type'],
        insertAfterCardId: string = '',
        partialCard: Partial<CardState> = {}
    ) =>
    (state: StoreState): StoreState =>
        buildAddCardResult(state, cardType, insertAfterCardId, partialCard)
            .nextState;

export const buildAddCardResult = (
    state: StoreState,
    cardType: CardState['type'],
    insertAfterCardId: string = '',
    partialCard: Partial<CardState> = {}
): AddCardResult => {
    const survey = state.survey;
    const newCard = generateCardShape(cardType, partialCard);

    let newAnswer: AnswerState | null = null;
    let newAnswerTwo: AnswerState | null = null;
    if (isQuestionItem(newCard)) {
        newCard.body = newCard.body || 'My Question';
        newAnswer = generateAnswerShape(cardType, { body: 'My 1st Choice' });
        newAnswerTwo = generateAnswerShape(cardType, { body: 'My 2nd Choice' });
        if (newAnswer && newAnswerTwo) {
            newCard.answerIds = [newAnswer.id, newAnswerTwo.id];
        }
    }

    const newCardIndex = insertAfterCardId
        ? survey.cardIds.indexOf(insertAfterCardId) + 1
        : survey.cardIds.length;

    const nextState = makeSurveyDirty({
        ...state,
        survey: {
            ...survey,
            cardIds: insert(survey.cardIds, newCardIndex, [newCard.id]),
        },
        cards: {
            ...state.cards,
            [newCard.id]: newCard,
        },
        answers:
            newAnswer && newAnswerTwo
                ? {
                      ...state.answers,
                      [newAnswer.id]: newAnswer,
                      [newAnswerTwo.id]: newAnswerTwo,
                  }
                : state.answers,
    });

    return { nextState, newCardId: newCard.id };
};

const generateCardShape = (
    cardType: CardState['type'],
    partialCard: Partial<CardState>
): CardState => {
    switch (cardType) {
        case 'linearQuestion':
            return mergeCardWithDefaults(
                { ...generateLinearQuestion(), answerIds: [] },
                partialCard as Partial<LinearQuestionState>
            );

        case 'forkedQuestion':
            return mergeCardWithDefaults(
                { ...generateForkedQuestion(), answerIds: [] },
                partialCard as Partial<ForkedQuestionState>
            );

        case 'titleCard':
            return mergeCardWithDefaults(
                generateTitleCard(),
                partialCard as Partial<TitleCardState>
            );

        case 'thankYouCard':
            return mergeCardWithDefaults(
                generateThankYouCard(),
                partialCard as Partial<ThankYouCardState>
            );

        case 'pollResultCard':
            return mergeCardWithDefaults(
                generatePollResult(),
                partialCard as Partial<PollResultCardState>
            );

        // Note: DynamicCoreQuestion is a special case that does not have `traxexLabel`.
        case 'dynamicCoreQuestion':
            return {
                ...generateDynamicCoreQuestion(),
                questionIds: [],
                ...(partialCard as Partial<DynamicCoreQuestionState>),
            };
    }
};

type CardWithTrackingLabel = { id: string; traxexLabel: string };

/** Merges a default card with a partial card, ensuring the id and traxexLabel are always set. */
const mergeCardWithDefaults = <T extends CardWithTrackingLabel>(
    defaultCard: T,
    partialCard: Partial<T>
): T => {
    const id = partialCard.id || defaultCard.id;

    return {
        ...defaultCard,
        ...partialCard,
        // Always use the card id as the fallback tracking label.
        id,
        traxexLabel: defaultCard.traxexLabel || partialCard.traxexLabel || id,
    };
};
